import { WORK_SCHEDULE_FLAG } from "@/pages/SettingsPage/WorkScheduleSettings/constants";
import { getPlannerSettings, isFeatureFlagEnabledWeb } from "@/redux/customers/customers.selectors";
import { getPlannerWorkingHours } from "@/redux/planner/planner.selectors";

import * as selectors from "./personalSettings.selectors";

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const MONDAY = 1;
const DEFAULT_DROP_MINUTES = 30;
const WHOLE_DAY = { startTime: "00:00:00", endTime: "23:59:00" };

const toMinutes = (time) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const END_OF_DAY = "23:59:00";

// Older company data stores an end-of-day finish as midnight.
const withEndOfDay = (hours) =>
  hours && toMinutes(hours.endTime) === 0 ? { ...hours, endTime: END_OF_DAY } : hours;

const isUsable = (hours) => {
  const start = toMinutes(hours?.startTime);
  const end = toMinutes(hours?.endTime);
  return start != null && end != null && start < end;
};

const companyWorkHours = (state) =>
  getPlannerSettings(state, { key: "PLANNER_WORK_HOURS" })?.value ?? null;

const saved = (state) => selectors.getSaved(state) ?? {};

export const isPersonalSettingsOn = (state) => isFeatureFlagEnabledWeb(state, WORK_SCHEDULE_FLAG);

// Hours shown as 24-hour clock strings, the shape the company Work Hours setting stores.
// With the flag off this is the company value, exactly as the Planner reads it today.
export const selectHoursShownValue = (state) => {
  if (!isPersonalSettingsOn(state)) {
    return companyWorkHours(state);
  }
  if (isUsable(saved(state).hoursShown)) {
    return saved(state).hoursShown;
  }
  const company = withEndOfDay(companyWorkHours(state));
  return isUsable(company) ? company : WHOLE_DAY;
};

// Hours shown as minutes from midnight, the shape the event forms read.
export const selectHoursShownMinutes = (state) => {
  if (!isPersonalSettingsOn(state)) {
    return getPlannerWorkingHours(state);
  }
  const hours = selectHoursShownValue(state);
  return { startTime: toMinutes(hours.startTime), endTime: toMinutes(hours.endTime) };
};

// 0 is Sunday. Unset stays Monday-first, as the Planner is today.
export const selectWeekStartsOn = (state) => {
  const weekStartsOn = saved(state).weekStartsOn;
  return isPersonalSettingsOn(state) && weekStartsOn != null ? weekStartsOn : MONDAY;
};

// The settings are only read with the flag on, so with it off the Monday start is final.
export const selectIsWeekStartKnown = (state) =>
  !isPersonalSettingsOn(state) || selectors.getIsLoaded(state) === true;

export const selectDaysShown = (state) => {
  const daysShown = saved(state).daysShown;
  return isPersonalSettingsOn(state) && daysShown?.length ? daysShown : ALL_DAYS;
};

// null means the drop keeps today's per-view length.
export const selectDropMinutes = (state) =>
  isPersonalSettingsOn(state) ? (saved(state).defaultTimeMinutes ?? DEFAULT_DROP_MINUTES) : null;
